// src/components/Header.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import api from '../api';
import styles from './Header.module.css';

// ✅ Logo desde src/assets
import logo from '../assets/logo.png'; // ajusta si usas .svg/.jpg

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();

  const [userName, setUserName] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const hydrateUserName = useCallback(async () => {
    const token = localStorage.getItem('api_token');
    const localName = localStorage.getItem('user_name');

    if (!token) {
      setUserName(null);
      return;
    }
    if (localName) {
      setUserName(localName);
      return;
    }
    try {
      const { data } = await api.get('/me');
      const name = data?.name || data?.user?.name || 'Estudiante';
      setUserName(name);
      localStorage.setItem('user_name', name);
    } catch {
      localStorage.removeItem('api_token');
      localStorage.removeItem('user_name');
      setUserName(null);
    }
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    const localName = localStorage.getItem('user_name');
    if (localName) setUserName(localName);
    hydrateUserName();
  }, [location, hydrateUserName]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'api_token' || e.key === 'user_name') {
        hydrateUserName();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [hydrateUserName]);

  const handleLogout = () => {
    localStorage.removeItem('api_token');
    localStorage.removeItem('user_name');
    setUserName(null);
    navigate('/login', { replace: true });
  };

  const links = [
    { to: '/', label: 'Inicio' },
    { to: '/sobre-nosotros', label: 'Nosotros' },
    { to: '/contact', label: 'Contacto' },
    { to: '/plans', label: 'Lista de productos' },
  ];

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <nav className={styles.nav}>

        {/* ===== Brand con imagen (desde src/assets) ===== */}
        <motion.div
          className={styles.logo}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.45 }}
        >
          <Link to="/" className={styles.brand} aria-label="Ir al inicio">
            <motion.img
              src={logo}
              alt="Logo"
              className={styles.brandImg}
              loading="eager"
              decoding="async"
              initial={{ opacity: 0, y: 2 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
            />
          </Link>
        </motion.div>

        {/* Desktop */}
        <div className={styles.desktop}>
          <div className={styles.links}>
            {links.map((l) => {
              const isActive = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`${styles.link} ${isActive ? styles.active : ''}`}
                >
                  {l.label}
                </Link>
              );
            })}
          </div>

          {!userName ? (
            <div className={styles.auth}>
              <Link
                to="/register"
                className={`${styles.link} ${location.pathname === '/register' ? styles.active : ''}`}
              >
                Registro
              </Link>
              <Link
                to="/login"
                className={`${styles.linkBtn} ${location.pathname === '/login' ? styles.btnActive : ''}`}
              >
                Ingresar
              </Link>
            </div>
          ) : (
            <div className={styles.auth}>
              <span className={styles.welcome}>Hola, {userName}</span>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                Cerrar
              </button>
            </div>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className={styles.menuToggle}
          onClick={() => setMenuOpen((s) => !s)}
          aria-label="Abrir menú"
        >
          {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>

        {/* Mobile drawer + overlay */}
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                className={styles.overlay}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMenuOpen(false)}
              />
              <motion.aside
                className={styles.drawer}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              >
                <div className={styles.drawerHeader}>
                  <Link to="/" onClick={() => setMenuOpen(false)} className={styles.brand} aria-label="Ir al inicio">
                    <img
                      src={logo}
                      alt="Logo"
                      className={styles.brandImgSm}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                  <button className={styles.close} onClick={() => setMenuOpen(false)} aria-label="Cerrar">
                    <FiX size={22} />
                  </button>
                </div>

                <div className={styles.drawerLinks}>
                  {links.map((l) => {
                    const isActive = location.pathname === l.to;
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        className={`${styles.drawerLink} ${isActive ? styles.drawerActive : ''}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {l.label}
                      </Link>
                    );
                  })}
                </div>

                <div className={styles.drawerAuth}>
                  {!userName ? (
                    <>
                      <Link to="/register" onClick={() => setMenuOpen(false)} className={styles.drawerLink}>
                        Registro
                      </Link>
                      <Link to="/login" onClick={() => setMenuOpen(false)} className={styles.drawerBtn}>
                        Ingresar
                      </Link>
                    </>
                  ) : (
                    <>
                      <span className={styles.welcome}>Hola, {userName}</span>
                      <button className={styles.drawerBtn} onClick={handleLogout}>
                        Cerrar
                      </button>
                    </>
                  )}
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
